import { inject, Injectable } from '@angular/core';
import {
  Params,
  PRIMARY_OUTLET,
  RouterStateSnapshot,
  TitleStrategy,
} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { OmniInsightsTitleService } from './omni-insights-title.service';

const QUERY_SECTION_LABELS: Record<string, string> = {
  request: 'Request Query/Report',
  'view-queries': 'View Queries/Reports',
  'create-layout': 'Create Custom Layout',
  'view-layouts': 'View Custom Layouts',
  'view-schedules': 'View Schedules',
  'view-completed': 'View Completed Queries',
  'view-suspended': 'View Suspended Queries/Reports',
  'schedule-only': 'Schedule Query',
};

const QUERY_STEP_LABELS: Record<string, string> = {
  type: 'Select query type',
  client: 'Select client',
  output: 'Select output',
  general: 'General parameters',
  save: 'Save query',
  schedule: 'Schedule',
};

const REPORTS_TAB_LABELS: Record<string, string> = {
  reports: 'Personalized',
  scheduled: 'Scheduled',
  completed: 'Completed',
  filters: 'Filters',
  'pending-reports': 'Shared reports',
  'pending-filters': 'Shared filters',
};

const REPORTS_STEP_LABELS: Record<string, string> = {
  report: 'Report',
  configure: 'Configure',
  filter: 'Filter',
  delivery: 'Delivery',
};

const ALERTS_TAB_LABELS: Record<string, string> = {
  messages: 'Alert Messages',
  'my-alerts': 'Manage Alerts',
};

const ALERTS_STEP_LABELS: Record<string, string> = {
  condition: 'Alert type',
  options: 'Options',
  delivery: 'Delivery',
};

@Injectable()
export class OmniInsightsTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly titleService = inject(OmniInsightsTitleService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const base = this.buildTitle(snapshot);
    if (base === undefined) {
      return;
    }

    const path = this.normalizePath(snapshot.url);

    if (!this.isSubmenuRoute(path)) {
      this.titleService.clearSubmenu();
      this.title.setTitle(base);
      return;
    }

    const submenu =
      this.resolveSubmenuFromQueryParams(path, snapshot) ??
      this.titleService.getSubmenu();
    this.title.setTitle(submenu ? `${base} - ${submenu}` : base);
  }

  private normalizePath(url: string): string {
    const path = url.split('?')[0].replace(/\/$/, '') || '/';
    return path.startsWith('/') ? path : `/${path}`;
  }

  private isSubmenuRoute(path: string): boolean {
    return (
      path === '/query' ||
      path === '/reports' ||
      path.startsWith('/reports/') ||
      path === '/alerts' ||
      path.startsWith('/alerts/')
    );
  }

  private resolveSubmenuFromQueryParams(
    path: string,
    snapshot: RouterStateSnapshot,
  ): string | null {
    const params = this.collectQueryParams(snapshot);

    if (path === '/query') {
      if (params['section']) {
        return QUERY_SECTION_LABELS[params['section']] ?? null;
      }
      if (params['step']) {
        return QUERY_STEP_LABELS[params['step']] ?? null;
      }
      return null;
    }

    if (path === '/reports') {
      if (params['tab']) {
        return REPORTS_TAB_LABELS[params['tab']] ?? null;
      }
      return null;
    }

    if (path.startsWith('/reports/')) {
      if (params['step']) {
        return REPORTS_STEP_LABELS[params['step']] ?? null;
      }
      return null;
    }

    if (path === '/alerts') {
      if (params['tab']) {
        return ALERTS_TAB_LABELS[params['tab']] ?? null;
      }
      return null;
    }

    if (path.startsWith('/alerts/')) {
      if (params['step']) {
        return ALERTS_STEP_LABELS[params['step']] ?? null;
      }
    }

    return null;
  }

  private collectQueryParams(snapshot: RouterStateSnapshot): Params {
    let params: Params = {};
    let route = snapshot.root;
    while (route) {
      params = { ...params, ...route.queryParams };
      route = route.children.find((child) => child.outlet === PRIMARY_OUTLET);
    }
    return params;
  }
}
